import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Money } from '@clari/ui'
import { ErrorBanner } from '@/components/ErrorBanner'
import { apiErrorMessage } from '@/lib/api-error'
import { adminApi } from '@/lib/admin-api'
import { formatDate } from '@/lib/format'

const FX_STATUSES = ['pending', 'completed', 'rejected', 'reversed']

function formatRate(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 4 }).format(value)
}

function formatPercent(value: number, locale: string): string {
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(value)}%`
}

export function FxConversions() {
  const { t, i18n } = useTranslation('admin')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)

  const query = useQuery({
    queryKey: ['admin-fx-conversions', status, page],
    queryFn: () =>
      adminApi.listFxConversions({
        ...(status ? { status } : {}),
        page,
      }),
  })
  const totalPages = query.data ? Math.max(1, Math.ceil(query.data.total / query.data.pageSize)) : 1

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-semibold">{t('fx.title')}</h1>
      <p className="text-text-muted">{t('fx.subtitle')}</p>

      <div className="flex flex-wrap items-end gap-3">
        <select
          className="clari-input max-w-xs"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value)
            setPage(1)
          }}
          aria-label={t('fx.status_filter')}
        >
          <option value="">{t('common.all')}</option>
          {FX_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {query.isError ? <ErrorBanner message={apiErrorMessage(query.error)} /> : null}
      {query.isLoading ? <p className="text-text-muted">{t('common.loading')}</p> : null}

      {query.data ? (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border text-text-muted">
              <tr>
                <th className="px-4 py-2 font-medium">{t('fx.col_user')}</th>
                <th className="px-4 py-2 font-medium">{t('fx.col_from')}</th>
                <th className="px-4 py-2 font-medium">{t('fx.col_to')}</th>
                <th className="px-4 py-2 font-medium">{t('fx.col_mid_rate')}</th>
                <th className="px-4 py-2 font-medium">{t('fx.col_applied_rate')}</th>
                <th className="px-4 py-2 font-medium">{t('fx.col_margin')}</th>
                <th className="px-4 py-2 font-medium">{t('fx.col_status')}</th>
                <th className="px-4 py-2 font-medium">{t('fx.col_date')}</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((c) => (
                <tr key={c.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2">
                    <Link to={`/users/${c.user_id}`} className="font-mono text-xs hover:underline">
                      {c.user_id.slice(0, 8)}
                    </Link>
                  </td>
                  <td className="px-4 py-2">
                    <Money
                      amountCents={c.source_amount_cents}
                      currency={c.source_currency}
                      locale={i18n.language}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Money
                      amountCents={c.destination_amount_cents}
                      currency={c.destination_currency}
                      locale={i18n.language}
                    />
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">
                    {formatRate(c.mid_market_rate, i18n.language)}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">
                    {formatRate(c.applied_rate, i18n.language)}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">
                    {formatPercent(c.margin_percent, i18n.language)}
                  </td>
                  <td className="px-4 py-2">{c.status}</td>
                  <td className="px-4 py-2 text-text-muted">
                    {formatDate(c.completed_at ?? c.created_at, i18n.language)}
                  </td>
                </tr>
              ))}
              {query.data.items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-text-muted">
                    {t('fx.empty')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="flex items-center gap-3 text-sm">
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          {t('common.prev')}
        </button>
        <span className="text-text-muted">{t('common.page', { page })}</span>
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          {t('common.next')}
        </button>
      </div>
    </div>
  )
}
